import React from 'react';
import { ArrowDown, User, Crown, Building2, Briefcase, Shield, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiGetJson } from '../../utils/api';

const ROLE_CONFIG = {
  admin: { icon: Crown, color: 'bg-red-500', ring: 'ring-red-200 dark:ring-red-900', label: 'Administrator' },
  director: { icon: Building2, color: 'bg-orange-500', ring: 'ring-orange-200 dark:ring-orange-900', label: 'Director' },
  manager: { icon: Briefcase, color: 'bg-purple-500', ring: 'ring-purple-200 dark:ring-purple-900', label: 'Manager' },
  associate: { icon: Shield, color: 'bg-blue-500', ring: 'ring-blue-200 dark:ring-blue-900', label: 'Associate' },
  user: { icon: Users, color: 'bg-gray-500', ring: 'ring-gray-200 dark:ring-gray-800', label: 'User' },
};

function ChainCard({ user, label, isYou, theme }) {
  const isDark = theme === 'dark';
  const config = ROLE_CONFIG[user?.role] || ROLE_CONFIG.user;
  const RoleIcon = config.icon;

  return (
    <div className={`inline-flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${
      isYou
        ? `border-blue-500 ${isDark ? 'bg-blue-950/30' : 'bg-blue-50'} shadow-sm`
        : `${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} shadow-sm`
    }`}>
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className={`w-8 h-8 rounded-full ring-2 ${config.ring}`} />
          ) : (
            <div className={`w-8 h-8 rounded-full ${config.color} ring-2 ${config.ring} flex items-center justify-center text-white font-medium text-sm`}>
              {(user?.first_name || user?.username || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${config.color} flex items-center justify-center ring-[1.5px] ${isDark ? 'ring-gray-800' : 'ring-white'}`}>
            <RoleIcon size={8} className="text-white" />
          </div>
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-1.5">
            <p className="font-unna font-semibold text-sm">
              {user?.first_name
                ? `${user.first_name} ${user.last_name || ''}`.trim()
                : user?.username || 'Unknown'}
            </p>
            {isYou && (
              <span className="text-[9px] font-rasa font-semibold px-1.5 py-px rounded-full bg-blue-500 text-white">
                You
              </span>
            )}
          </div>
          <p className={`text-[11px] font-rasa leading-tight ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {config.label}{label ? ` · ${label}` : ''}
          </p>
        </div>
    </div>
  );
}

function ChainConnector({ theme }) {
  const isDark = theme === 'dark';
  return (
    <div className="flex flex-col items-center py-0.5">
      <div className={`w-px h-2.5 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
      <ArrowDown size={12} className={isDark ? 'text-gray-600' : 'text-gray-300'} />
      <div className={`w-px h-2.5 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
    </div>
  );
}

export default function MyChainPanel({ chain, directSupervisors, currentUser, theme }) {
  const isDark = theme === 'dark';
  const hasChain = chain.length > 0 || directSupervisors.length > 0;

  const reversedChain = [...chain].reverse();
  const extraSupervisors = directSupervisors.filter(s => !chain.some(c => c.id === s.id));

  // Pull the current user's department assignments from the management hierarchy
  // endpoint, which includes a `departments` array per serialized user.
  const { data: hierarchyData } = useQuery({
    queryKey: ['management', 'hierarchy', 'me-departments'],
    queryFn: () => apiGetJson('/management/hierarchy'),
    enabled: !!currentUser?.username,
    staleTime: 5 * 60 * 1000,
  });
  const myDepartments = (hierarchyData?.hierarchy || [])
    .find((u) => u.username === currentUser?.username)?.departments || [];

  return (
    <div className="max-w-xs mx-auto">
      <div className="text-center mb-6">
        <h2 className="font-dm-serif font-semibold">Reporting Chain</h2>
        {hasChain && (
          <p className={`text-[11px] font-rasa mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Your position in the hierarchy
          </p>
        )}
      </div>

      {myDepartments.length > 0 && (
        <div className={`mb-5 rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className={`text-[10px] uppercase tracking-wide font-rasa mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Your department{myDepartments.length > 1 ? 's' : ''}
          </div>
          <div className="space-y-1.5">
            {myDepartments.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-sm font-rasa">
                <Building2 size={12} className="text-blue-500" />
                <span>{d.department_name}</span>
                {d.service_area_name && (
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>· {d.service_area_name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasChain ? (
        <div className={`text-center py-12 rounded-xl border border-dashed ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <User size={36} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm font-rasa text-gray-500">No reporting chain assigned</p>
          <p className="text-[11px] font-rasa text-gray-400 mt-1">A director or admin will assign your structure</p>
        </div>
      ) : (
        <div className="flex flex-col items-center space-y-0">
          {reversedChain.map((supervisor, index) => (
            <div key={supervisor.id}>
              <ChainCard
                user={supervisor}
                label={index === 0 ? 'Top of chain' : undefined}
                theme={theme}
              />
              <ChainConnector theme={theme} />
            </div>
          ))}

          {extraSupervisors.map(supervisor => (
            <div key={supervisor.id}>
              <ChainCard
                user={supervisor}
                label="Direct supervisor"
                theme={theme}
              />
              <ChainConnector theme={theme} />
            </div>
          ))}

          <ChainCard
            user={{
              first_name: currentUser?.firstName || currentUser?.first_name,
              last_name: currentUser?.lastName || currentUser?.last_name,
              username: currentUser?.username,
              role: currentUser?.role,
              avatar_url: currentUser?.avatarUrl || currentUser?.avatar_url,
            }}
            isYou
            theme={theme}
          />
        </div>
      )}
    </div>
  );
}
